import type {
  CachedState,
  ColorOption,
  ViewTypes,
  AccessoryCategory,
  ModelsState,
  StreamTypes,
} from "types/app";

export const LOCAL_APP_NAME = "mahindra_born_360";

/**
 * The ID of the root div element for rendering react component tree.
 */
export const ROOT_DIV_ID = "excite_360";

const PATH = (document.getElementById(ROOT_DIV_ID) as HTMLDivElement).getAttribute("path") || "";

/**
 * Dynamic base URL for the app, can be set in the root div element using the 'path' attribute.
 */
export const DYNAMIC_BASE_URL = PATH.substring(PATH.length - 1) === "/" ? PATH : PATH + "/";

/**
 * Paths to external (public) assets
 */
export const EXTERNAL_ICONS = {
  START_LOADING: `${DYNAMIC_BASE_URL}resources/start-loading.svg`,
  LOADING_OVERLAY_CIRCLE_IMG: `${DYNAMIC_BASE_URL}resources/loading-circle.gif`,
  LOADING_OVERLAY_VEHICLE_IMG: `${DYNAMIC_BASE_URL}resources/loading-vehicle.png`,
  "BE 6_VEHICLE_IMG": `${DYNAMIC_BASE_URL}resources/LoadingScreen/BE6_LoadingScreen.png`,
  "XEV 9e_VEHICLE_IMG": `${DYNAMIC_BASE_URL}resources/LoadingScreen/XEV9E_LoadingScreen.png`,
};

export const DESKTOP_DRAWER_OPEN_WIDTH = 360;

/**
 * The URL to the file containing the initial & default configuration data.
 */
export const CONFIGURATION_FILE_URL = `${DYNAMIC_BASE_URL}resources/configuration.json`;

/**
 * The URL to the file containing data for all the available variants.
 */
export const VARIANTS_FILE_URL = `${DYNAMIC_BASE_URL}resources/variants.json`;

/**
 * The URL to the file containing data for all the available features.
 */
export const FEATURES_AND_COLORS_FILE_URL = `${DYNAMIC_BASE_URL}resources/features-and-colors.json`;

/**
 * The URL to the file containing data for all the available accessories.
 */
export const ACCESSORIES_FILE_URL = `${DYNAMIC_BASE_URL}resources/accessories.json`;

/**
 * The URL to the file containing data for all the available scenario-variants.
 */
export const SCENARIO_VARIANTS_FILE_URL = `${DYNAMIC_BASE_URL}resources/scenario-variants.json`;

/**
 * The available view types.
 */
export const VIEW_TYPES: Record<string, ViewTypes> = {
  EXTERIOR: "exterior",
  INTERIOR: "interior",
} as const;

/**
 * The available accessory types for variants
 */
export const ACCESSORY_TYPES = {
  KIT: "kit" as AccessoryCategory.KIT,
  EXTERIOR: "exterior" as AccessoryCategory.EXTERIOR,
  INTERIOR: "interior" as AccessoryCategory.INTERIOR,
} as const;

export const ENV_TYPES = {
  ROOFTOP_ID: "5g1xe9qn4vlj3pm", // ROOFTOP
  STUDIO_ID: "j6y250qj9vrenxw", // STUDIO
} as const;

/**
 * Models
 */
export const MODELS: ModelsState = {
  "BE 6": {
    id: process.env.REACT_APP_MODELYEAR_ID_1,
    code: "BE 6",
    variantId: "be6-pc-79-5-r19",
  },
  "XEV 9e": {
    id: process.env.REACT_APP_MODELYEAR_ID_2,
    code: "XEV 9e",
    variantId: "xev9e-pc-79-5-r19",
  },
} as const;

export const MODELS_NAMES = {
  m110: "BE 6",
  m310: "XEV 9e",
};

type ModelKeys = keyof typeof MODELS;
type SunStateType = {
  dayTime: number;
  northyaw: number;
};

export type AnimationType = {
  name: string;
  duration: number;
};

// Utility function to get model codes dynamically
const getModelCode = (modelKey: ModelKeys) => MODELS[modelKey].code;

export const SUN_STATE: Record<string, { dayMode: SunStateType; nightMode: SunStateType }> = {
  [getModelCode("BE 6")]: {
    dayMode: { dayTime: 8, northyaw: 80 },
    nightMode: { dayTime: 19.75, northyaw: 100 },
  },
  [getModelCode("XEV 9e")]: {
    dayMode: { dayTime: 8, northyaw: 80 },
    nightMode: { dayTime: 19.75, northyaw: 100 },
  },
};

const commonAnimations = {
  gloveBoxStream: { name: "ANIM_GLOVEBOX", duration: 3 },
};

// BE6:
// ANIM_DOORS_ALL, ANIM_CHARGING_PORT_DOOR, ANIM_SEAT_ARM_REST, ANIM_CENTRE_ARM_REST, ANIM_M110_HELLO, ANIM_M110_MARS, ANIM_SUNROOF_BLIND, ANIM_M110_GOODBYE

// XEV:
// ANIM_DOOR_ALL, ANIM_CHARGING_PORT_DOOR, ANIM_CENTER_ARMREST, ANIM_2nd_ROW_SEAT, ANIM_SEAT_ARMREST, ANIM_M310_HELLO, ANIM_M310_MARS, ANIM_PANOROOF_BLIND, ANIM_M310_GL_GOODBYE

export const turntable_be6_sequence = [
  "M110_TURNTABLE_DOORS",
  "ANIM_M110_HELLO",
  "ANIM_M110_MARS",
  "M110_TURNTABLE_SUNROOF",
  "ANIM_M110_GOODBYE",
];

export const turntable_xev_9e_sequence = [
  "M310_TURNTABLE_DOORS",
  "ANIM_M310_HELLO",
  "ANIM_M310_MARS",
  "M310_TURNTABLE_PANO",
  "ANIM_M310_GL_GOODBYE",
];

export const ANIMATIONS: Record<string, Record<string, AnimationType>> = {
  [getModelCode("BE 6")]: {
    ...commonAnimations,
    // welcomeHello: { name: "ANIM_M110_HELLO", duration: 32 },
    openDoorsStream: { name: "ANIM_DOORS_ALL", duration: 3 },
    chargingPortDoorStream: { name: "ANIM_CHARGING_PORT_DOOR", duration: 3 },
    sunroofBlindStream: { name: "ANIM_SUNROOF_BLIND", duration: 10 },
    trunkOpenStream: { name: "ANIM_TRUNK_60d", duration: 3 },
    centerArmRestStream: { name: "ANIM_CENTRE_ARM_REST", duration: 3 },
    seatArmRestStream: { name: "ANIM_SEAT_ARM_REST", duration: 3 },
    // marsRoof: { name: "ANIM_M110_MARS", duration: 3 },
    // goodByeHello: { name: "ANIM_M110_GOODBYE", duration: 3 },
    symphonyRevStream: { name: "ANIM_SYMPH", duration: 1.5 },

    welcomeHello: { name: "ANIM_M110_HELLO", duration: 32 },
    openDoors: { name: "M110_TURNTABLE_DOORS", duration: 3 },
    sunroofBlind: { name: "M110_TURNTABLE_SUNROOF", duration: 10 },
    marsRoof: { name: "ANIM_M110_MARS", duration: 3 },
    goodByeHello: { name: "ANIM_M110_GOODBYE", duration: 3 },
  },
  [getModelCode("XEV 9e")]: {
    ...commonAnimations,
    openDoorsStream: { name: "ANIM_DOOR_ALL", duration: 3 },
    chargingPortDoorStream: { name: "ANIM_CHARGING_PORT", duration: 3 },
    secondRowSeatStream: { name: "ANIM_2nd_ROW_SEAT", duration: 5 },
    sunroofBlindStream: { name: "ANIM_PANOROOF_BLIND", duration: 3 },
    centerArmRestStream: { name: "ANIM_CENTER_ARMREST", duration: 3 },
    seatArmRestStream: { name: "ANIM_SEAT_ARMREST", duration: 3 },

    welcomeHello: { name: "ANIM_M310_HELLO", duration: 32 },
    openDoors: { name: "M310_TURNTABLE_DOORS", duration: 3 },
    sunroofBlind: { name: "M310_TURNTABLE_PANO", duration: 3 },
    marsRoof: { name: "ANIM_M310_MARS", duration: 3 },
    goodByeHello: { name: "ANIM_M310_GL_GOODBYE", duration: 3 },
  },
};

export const DEFAULT_MODEL_KEY = "BE 6";
export const ALLOWED_QUERIES = ["BE-6", "XEV-9e"];

/**
 * The default state of the app. Overwritten by the configuration JSON file.
 */
export const DEFAULT_CACHED_STATE: CachedState = {
  model: MODELS["BE 6"],
  variantId: "",
  view: VIEW_TYPES.EXTERIOR,
  colorId: "",
  animations: {
    dayMode: true,
    doorsOpen: false,
    headlights: false,
    sunroofOpen: false,
  },
  timestamp: Date.now(),
} as const;

export enum APP_STATES {
  UNLOADED,
  INITIAL_LOADING,
  VARIANT_LOADING,
  LOADED,
}

export const DEFAULT_AVAILABLE_COLORS: ColorOption[] = [];

/**
 * The available streaming types for 3d model.
 */
export const STREAM_TYPES: Record<string, StreamTypes> = {
  STREAMING: "streaming",
  TURNTABLE: "turntable",
  IN_GLO: "in-glo",
} as const;

export const CAR_DOOR_OPEN_SOUND = `${DYNAMIC_BASE_URL}assets/mp3/CarDoorOpen.mp3`;
export const CAR_DOOR_CLOSE_SOUND = `${DYNAMIC_BASE_URL}assets/mp3/CarDoorClose.mp3`;

export enum CAMERA_PREFIX_NAMES {
  EXT = "turn_EXT_",
  Driver = "turn_INT_D_",
  Passenger = "turn_INT_P_",
  Rear = "turn_INT_R_",
}

export const EXTERIOR_CAMERA_NAMES = ["Cam_orbit_01EXT"];

export const INTERIOR_CAMERA_NAMES = ["Cam_orbit_01INT", "Cam_orbit_02INT", "Cam_orbit_03INT"];

export const INTERIOR_CAMERA_LABELS = ["Driver", "Passenger", "Rear"];
export const EXTERIOR_CAMERA_LABELS = ["Main"];
